import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Spinner, Badge, Button, Form, InputGroup } from 'react-bootstrap';
import { fetchPublicClasses, setClassFilter } from '../../store/slices/classesSlice';
import { fetchProfile } from '../../store/slices/profileSlice';
import './classes.scss';

// ── Fallback image ──
const CLASS_FALLBACK = 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=500&h=280&fit=crop';

const CLASS_TYPES = [
  // { label: 'All Classes', value: '', icon: '🌐' },
  // { label: '1-on-1 Live', value: 'one_to_one', icon: '👤' },
  // { label: 'Group Live', value: 'group', icon: '👥' },
];

const ClassCard = ({ cls, onBook, isLoading }) => {
  const navigate = useNavigate();
  const isOneOnOne = cls.type === 'one_to_one';

  const localStart = cls.start_date ? new Date(cls.start_date) : null;
  const localEnd = cls.end_date ? new Date(cls.end_date) : null;

  const startDate = localStart && !isNaN(localStart.getTime())
    ? localStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'TBD';

  const startTime = localStart && !isNaN(localStart.getTime())
    ? localStart.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  const endTime = localEnd && !isNaN(localEnd.getTime())
    ? localEnd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  const handleClick = () => {
    navigate(`/class-detail/${cls._id}`);
  };

  const isObjectId = (val) => typeof val === 'string' && /^[0-9a-fA-F]{24}$/.test(val);
  const languageName = (cls.language && typeof cls.language === 'object')
    ? cls.language.name
    : (cls.language && !isObjectId(cls.language) ? cls.language : 'English');

  const instructor = cls.instructor || cls.teacher;
  const teacherName = (instructor && typeof instructor === 'object' && instructor.full_name)
    ? instructor.full_name
    : 'Yoospeak Instructor';
  const teacherId = (instructor && typeof instructor === 'object' && instructor._id)
    ? instructor._id
    : (typeof instructor === 'string' ? instructor : '');
  const teacherAvatar = (instructor && typeof instructor === 'object' && instructor.profile_picture)
    ? instructor.profile_picture
    : 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=40&h=40&fit=crop';

  return (
    <div className="community-card card-hover bg-white border h-100 rounded-4 overflow-hidden pointer" onClick={handleClick}>
      <div className="position-relative">
        <img
          src={cls.cover_image || cls.image || CLASS_FALLBACK}
          alt={cls.title}
          className="img-fluid w-100 object-fit-cover"
          style={{ height: "180px" }}
        />
        {/* <span className={`badge-live d-flex align-items-center justify-content-center gap-1 position-absolute top-0 end-0 m-2 text-xs text-white fw-bold px-3 py-1 rounded-pill shadow-sm z-3 ${isOneOnOne ? 'bg-success' : 'bg-primary-purple'}`}>
          <i className={`bi ${isOneOnOne ? 'bi-person-fill' : 'bi-people-fill'} me-1`}></i>
          {isOneOnOne ? '1-on-1' : 'Group'}
        </span> */}
      </div>

      <div className="item-info p-3">
        {/* Instructor info */}
        {instructor && (
          <Link
            to={teacherId ? `/teachers/${teacherId}` : '#'}
            className="card-owner-row d-flex align-items-center gap-2 mb-2 text-decoration-none"
            onClick={(e) => {
              if (!teacherId) {
                e.preventDefault();
              }
              e.stopPropagation();
            }}
          >
            <div className="owner-avatar rounded-circle overflow-hidden border border-light-subtle shadow-sm" style={{ width: '28px', height: '28px', flexShrink: 0 }}>
              <img
                src={teacherAvatar}
                alt={teacherName}
                className="w-100 h-100 object-fit-cover"
              />
            </div>
            <span className="owner-name text-xs text-gray-500 text-truncate d-flex align-items-center gap-1">
              {teacherName}
              {instructor?.is_blue_verified && <i className="bi bi-patch-check-fill text-primary" style={{ fontSize: '0.75rem' }} title="Verified Teacher" />}
            </span>
          </Link>
        )}

        {/* Title */}
        <h5 className="card-title fw-bold text-dark text-truncate mb-2" title={cls.title || 'Untitled Class'}>
          {cls.title || 'Untitled Class'}
        </h5>

        {/* Description */}
        {cls.description && (
          <p className="card-description text-gray-600 text-xs mb-3 text-truncate-2">
            {cls.description}
          </p>
        )}

        {/* Meta tags */}
        <div className="card-meta-row d-flex flex-wrap align-items-center gap-2 mb-3">
          <span className="meta-item text-xs text-gray-500">
            <i className="bi bi-calendar3 text-primary"></i> {startDate}
          </span>
          {startTime && (
            <span className="meta-item text-xs text-gray-500 ms-2">
              <i className="bi bi-clock text-primary"></i> {startTime} - {endTime}
            </span>
          )}
          <span className="meta-item text-xs text-gray-500 ms-2">
            <i className="bi bi-translate text-primary"></i> {languageName}
          </span>
        </div>

        {/* Footer */}
        <div className="card-footer-action pt-2 border-top d-flex align-items-center justify-content-between">
          <span className="fee-text text-sm fw-bold text-dark">
            {cls.cost > 0 ? `$${cls.cost}` : 'Free'}
          </span>
          <Button
            className="btn-join-community text-white rounded-pill px-3 py-1 text-xs fw-bold border-0"
            onClick={(e) => { e.stopPropagation(); onBook(cls._id); }}
            disabled={isLoading}
          >
            {isLoading ? 'Booking...' : 'Book Now'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const ClassesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const teacherIdParam = searchParams.get('teacherId');

  const { classes, loading, error, filters } = useSelector((state) => state.classes);
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { profile } = useSelector((state) => state.profile);
  const [searchInput, setSearchInput] = useState('');
  const [bookingId, setBookingId] = useState(null);

  useEffect(() => {
    dispatch(fetchPublicClasses());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated && !profile) {
      dispatch(fetchProfile());
    }
  }, [isAuthenticated, profile, dispatch]);

  const isTeacherOrAdmin = isAuthenticated && profile && (profile.user_type === 'teacher' || profile.user_type === 'admin' || profile.is_admin);

  const handleCreateClassClick = () => {
    navigate('/create-class');
  };

  // Client-side filtering
  const filtered = classes.filter((cls) => {
    const matchType = !filters.type || cls.type === filters.type;
    const matchSearch = !filters.search ||
      cls.title?.toLowerCase().includes(filters.search.toLowerCase()) ||
      (cls.instructor?.full_name || cls.teacher?.full_name)?.toLowerCase().includes(filters.search.toLowerCase());

    const instructor = cls.instructor || cls.teacher;
    const instructorId = (instructor && typeof instructor === 'object')
      ? (instructor._id || instructor.id)
      : instructor;
    const actualTeacherId = cls.teacher_id || instructorId;
    const matchTeacher = !teacherIdParam ||
      (actualTeacherId && actualTeacherId.toString() === teacherIdParam.toString());

    return matchType && matchSearch && matchTeacher;
  });

  const handleSearch = (e) => {
    e.preventDefault();
    dispatch(setClassFilter({ key: 'search', value: searchInput }));
  };

  const handleBook = (classId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setBookingId(classId);
    setTimeout(() => setBookingId(null), 1500);
  };

  return (
    <div className="communities-page">
      {/* Hero Section */}
      <section className="hero-banner py-5">
        <Container fluid="xl">
          <Row className="g-4">
            <Col lg={5} md={6}>
              <div className="content-wrapper">
                <div className="hero-heading mb-3">
                  Explore <span className="highlight-purple">Live Classes</span>
                </div>
                <div className="hero-subtitle mb-3">
                  Join expert-led live sessions — 1-on-1 or group formats available. Book a class and start speaking with confidence.
                </div>
                {isTeacherOrAdmin && (
                  <Button variant="purple" className="btn-create-community rounded-pill btn-custom" onClick={handleCreateClassClick}>
                    <i className="bi bi-plus-lg"></i> Create Class
                  </Button>
                )}
              </div>
            </Col>

            <Col lg={7} md={6}>
              <Row className="hero-features-row g-2 g-lg-3">
                <Col lg={6} xl={4}>
                  <div className="hero-feature-card d-flex align-items-start gap-3">
                    <div className="feature-icon rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', flexShrink: 0 }}>
                      <i className="bi bi-camera-video-fill fs-4"></i>
                    </div>
                    <div>
                      <div className="base-heading">Live Sessions</div>
                      <div className="feature-desc text-muted">Join live video calls with expert tutors.</div>
                    </div>
                  </div>
                </Col>
                <Col lg={6} xl={4}>
                  <div className="hero-feature-card d-flex align-items-start gap-3">
                    <div className="feature-icon rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', flexShrink: 0 }}>
                      <i className="bi bi-person-fill fs-4"></i>
                    </div>
                    <div>
                      <div className="base-heading">1-on-1 Tutors</div>
                      <div className="feature-desc text-muted">Get customized focus and personal feedback.</div>
                    </div>
                  </div>
                </Col>
                <Col xl={4}>
                  <div className="hero-feature-card d-flex align-items-start gap-3">
                    <div className="feature-icon rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', flexShrink: 0 }}>
                      <i className="bi bi-people-fill fs-4"></i>
                    </div>
                    <div>
                      <div className="base-heading">Group Lessons</div>
                      <div className="feature-desc text-muted">Practice conversation with peers globally.</div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Filter and Search Bar Section */}
      <section className="categories-section bg-white section-padding pb-0">
        <Container fluid="xl">
          {/* Search bar inside content */}
          <div className="search-box-wrapper mx-auto mb-4" style={{ maxWidth: "600px" }}>
            <Form onSubmit={handleSearch}>
              <InputGroup className="bg-white border rounded-pill shadow-sm overflow-hidden px-2 py-1">
                <InputGroup.Text className="bg-transparent border-0 shadow-none text-gray-700">
                  <i className="bi bi-search"></i>
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search classes by title or instructor..."
                  className="border-0 shadow-none px-2"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                {searchInput && (
                  <InputGroup.Text className="bg-transparent border-0 shadow-none text-gray-700">
                    <i className="bi bi-x-circle clear-icon pointer" onClick={() => {
                      setSearchInput('');
                      dispatch(setClassFilter({ key: 'search', value: '' }));
                    }}></i>
                  </InputGroup.Text>
                )}
              </InputGroup>
            </Form>
          </div>

          <div className="categories-filter-bar mb-4">
            <div className="categories-scroll-wrapper">
              <div className="categories-pills">
                {CLASS_TYPES.map((t) => (
                  <button
                    key={t.value}
                    onClick={() => dispatch(setClassFilter({ key: 'type', value: t.value }))}
                    className={`category-pill-btn d-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-medium ${filters.type === t.value ? 'active' : ''}`}
                    style={{ cursor: 'pointer' }}
                  >
                    <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>{t.icon}</span>
                    <span>{t.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Classes Grid Section */}
      <section className="communities-grid-section bg-white section-padding pt-0">
        <Container fluid="xl">
          {/* Results count */}
          {!loading.list && (
            <div className="results-count mb-4 text-gray-600 fw-medium">
              <i className="bi bi-grid-3x3-gap me-2 text-primary"></i>
              {filtered.length} class{filtered.length !== 1 ? 'es' : ''} available
            </div>
          )}

          {/* Loading */}
          {loading.list && (
            <div className="d-flex justify-content-center align-items-center py-5">
              <div className="text-center">
                <Spinner animation="border" variant="primary" className="loading-spinner" />
                <p className="mt-3 text-gray-500 fw-medium">Loading classes...</p>
              </div>
            </div>
          )}

          {/* Error */}
          {error.list && !loading.list && (
            <div className="alert alert-danger rounded-3 text-center py-4">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error.list}
              <div className="mt-3">
                <Button variant="outline-danger" size="sm" onClick={() => dispatch(fetchPublicClasses())}>
                  Try Again
                </Button>
              </div>
            </div>
          )}

          {/* Empty state */}
          {!loading.list && !error.list && filtered.length === 0 && (
            <div className="empty-state text-center py-5">
              <div className="empty-icon mb-4">
                <i className="bi bi-camera-video-off text-primary empty-state-icon"></i>
              </div>
              <h3 className="fw-bold text-gray-900 mb-2">No classes found</h3>
              <p className="text-gray-500 mb-4">Try adjusting your filters or search terms.</p>
              <Button variant="primary" onClick={() => {
                setSearchInput('');
                dispatch(setClassFilter({ key: 'search', value: '' }));
                dispatch(setClassFilter({ key: 'type', value: '' }));
              }}>Clear Filters</Button>
            </div>
          )}

          {/* Grid */}
          {!loading.list && filtered.length > 0 && (
            <Row className="g-4 row-cols-1 row-cols-md-2 row-cols-xl-3">
              {filtered.map((cls) => (
                <Col key={cls._id}>
                  <ClassCard
                    cls={cls}
                    onBook={handleBook}
                    isLoading={bookingId === cls._id}
                  />
                </Col>
              ))}
            </Row>
          )}
        </Container>
      </section>

      {/* CTA — App Banner */}
      <div className="app-cta-banner py-5">
        <Container fluid="xl">
          <div className="cta-inner text-center">
            <h2 className="fw-black text-white mb-2">Want more options?</h2>
            <p className="text-white text-opacity-75 mb-4">
              Browse exclusive 1-on-1 sessions and book directly in the Yoospeak app.
            </p>
            <div className="d-flex flex-wrap justify-content-center gap-3">
              <a
                href="https://play.google.com/store/apps/details?id=com.yoospeak.learning"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={require('../../assets/images/play-store.png')}
                  alt="Google Play"
                  className="img-fluid rounded-3 app-store-badge"
                />
              </a>
              <a
                href="https://apps.apple.com/in/app/yoospeak/id6746839854"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={require('../../assets/images/app-store.png')}
                  alt="App Store"
                  className="img-fluid rounded-3 app-store-badge"
                />
              </a>
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
};
